import { useEffect, useMemo, useState } from 'react';
import SectionCard from '../components/SectionCard';

const createEmptyOfferForm = () => ({
  client_id: '',
  name: '',
  description: '',
  typecpa: '',
  parameters: '',
  status: 'edit',
  balance: '',
  url: '',
  baseurl: '',
  botname: '',
  typeoffer: '',
  secretkey: '',
  banner: ''
});

const mapOfferToForm = (offer) => ({
  client_id: offer.client_id ?? '',
  name: offer.name ?? '',
  description: offer.description ?? '',
  typecpa: offer.typecpa ?? '',
  parameters: offer.parameters ?? '',
  status: offer.status ?? 'edit',
  balance: offer.balance ?? '',
  url: offer.url ?? '',
  baseurl: offer.baseurl ?? '',
  botname: offer.botname ?? '',
  typeoffer: offer.typeoffer ?? '',
  secretkey: offer.secretkey ?? '',
  banner: offer.banner ?? ''
});

const normalizeOfferPayload = (form) => {
  const normalizedClientId = Number.parseInt(form.client_id, 10);
  if (!Number.isInteger(normalizedClientId)) {
    throw new Error('client_id должен быть целым числом');
  }

  const normalizedName = String(form.name || '').trim();
  if (!normalizedName) {
    throw new Error('Название оффера обязательно');
  }

  let normalizedParameters = null;
  if (String(form.parameters || '').trim() !== '') {
    normalizedParameters = Number.parseInt(form.parameters, 10);
    if (!Number.isInteger(normalizedParameters)) {
      throw new Error('parameters должен быть целым числом');
    }
  }

  let normalizedBalance = 0;
  if (String(form.balance || '').trim() !== '') {
    normalizedBalance = Number.parseFloat(form.balance);
    if (Number.isNaN(normalizedBalance)) {
      throw new Error('balance должен быть числом');
    }
  }

  const nullableString = (value) => {
    const trimmed = String(value || '').trim();
    return trimmed === '' ? null : trimmed;
  };

  return {
    client_id: normalizedClientId,
    name: normalizedName,
    description: nullableString(form.description),
    typecpa: nullableString(form.typecpa),
    parameters: normalizedParameters,
    status: String(form.status || 'edit').trim() || 'edit',
    balance: normalizedBalance,
    url: nullableString(form.url),
    baseurl: nullableString(form.baseurl),
    botname: nullableString(form.botname),
    typeoffer: nullableString(form.typeoffer),
    secretkey: nullableString(form.secretkey),
    banner: nullableString(form.banner)
  };
};

function ProductsPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(createEmptyOfferForm());
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editOfferId, setEditOfferId] = useState(null);
  const [editForm, setEditForm] = useState(createEmptyOfferForm());

  const sortedOffers = useMemo(() => {
    return [...offers].sort((a, b) => (b.id || 0) - (a.id || 0));
  }, [offers]);

  const loadOffers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/admin/offers', {
        method: 'GET',
        credentials: 'include'
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Не удалось загрузить офферы');
      }

      setOffers(Array.isArray(payload) ? payload : []);
    } catch (loadError) {
      setError(loadError.message || 'Не удалось загрузить офферы');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffers();
  }, []);

  const handleCreateChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setSubmittingCreate(true);
    setError('');

    try {
      const payload = normalizeOfferPayload(createForm);
      const response = await fetch('/api/admin/offers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.error || 'Не удалось создать оффер');
      }

      setCreateForm(createEmptyOfferForm());
      setCreateOpen(false);

      await loadOffers();
    } catch (createError) {
      setError(createError.message || 'Не удалось создать оффер');
    } finally {
      setSubmittingCreate(false);
    }
  };

  const handleDelete = async (offerId) => {
    const confirmed = window.confirm(`Удалить оффер #${offerId}?`);
    if (!confirmed) {
      return;
    }

    setError('');
    try {
      const response = await fetch(`/api/admin/offers/${offerId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Не удалось удалить оффер');
      }

      setOffers((prev) => prev.filter((offer) => offer.id !== offerId));
    } catch (deleteError) {
      setError(deleteError.message || 'Не удалось удалить оффер');
    }
  };

  const openEditModal = (offer) => {
    setEditOfferId(offer.id);
    setEditForm(mapOfferToForm(offer));
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditOfferId(null);
    setEditForm(createEmptyOfferForm());
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editOfferId) {
      return;
    }

    setSubmittingEdit(true);
    setError('');
    try {
      const payloadBody = normalizeOfferPayload(editForm);

      const response = await fetch(`/api/admin/offers/${editOfferId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payloadBody)
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Не удалось обновить оффер');
      }

      const updatedOffer = payload.offer;
      setOffers((prev) => prev.map((item) => (item.id === updatedOffer.id ? updatedOffer : item)));
      closeEditModal();
    } catch (editError) {
      setError(editError.message || 'Не удалось обновить оффер');
    } finally {
      setSubmittingEdit(false);
    }
  };

  return (
    <div className="page-grid">
      <SectionCard title="Офферы" subtitle="Список офферов и управление карточками">
        {error && <p className="offer-error">{error}</p>}
        <div className="offers-toolbar">
          <button type="button" className="accent-button" onClick={() => setCreateOpen((prev) => !prev)}>
            {createOpen ? 'Скрыть форму' : 'Создать оффер'}
          </button>
        </div>

        {createOpen ? (
          <form className="offer-form offer-form-panel" onSubmit={handleCreate}>
            <label>
              ID клиента *
              <input
                name="client_id"
                value={createForm.client_id}
                onChange={handleCreateChange}
                placeholder="1001"
                required
              />
            </label>
            <label>
              Название *
              <input
                name="name"
                value={createForm.name}
                onChange={handleCreateChange}
                placeholder="Название оффера"
                required
              />
            </label>
            <label>
              Статус
              <select name="status" value={createForm.status} onChange={handleCreateChange}>
                <option value="edit">edit</option>
                <option value="active">active</option>
                <option value="paused">paused</option>
                <option value="archived">archived</option>
              </select>
            </label>
            <label>
              Тип CPA
              <input name="typecpa" value={createForm.typecpa} onChange={handleCreateChange} placeholder="revshare" />
            </label>
            <label>
              Параметры (integer)
              <input name="parameters" value={createForm.parameters} onChange={handleCreateChange} placeholder="30" />
            </label>
            <label>
              Баланс (float)
              <input name="balance" value={createForm.balance} onChange={handleCreateChange} placeholder="0.00" />
            </label>
            <label>
              Базовый URL
              <input name="baseurl" value={createForm.baseurl} onChange={handleCreateChange} placeholder="https://example.com/start?ref=" />
            </label>
            <label>
              URL
              <input name="url" value={createForm.url} onChange={handleCreateChange} placeholder="https://example.com/callback" />
            </label>
            <label>
              Имя бота
              <input name="botname" value={createForm.botname} onChange={handleCreateChange} placeholder="top_partner_bot" />
            </label>
            <label>
              Тип оффера
              <input name="typeoffer" value={createForm.typeoffer} onChange={handleCreateChange} placeholder="cpa" />
            </label>
            <label>
              Секретный ключ
              <input name="secretkey" value={createForm.secretkey} onChange={handleCreateChange} placeholder="secret" />
            </label>
            <label>
              Баннер (URL)
              <input name="banner" value={createForm.banner} onChange={handleCreateChange} placeholder="https://example.com/banner.png" />
            </label>
            <label className="offer-form-wide">
              Описание
              <textarea name="description" value={createForm.description} onChange={handleCreateChange} placeholder="Короткое описание" rows="3" />
            </label>
            <button className="accent-button offer-form-button" type="submit" disabled={submittingCreate}>
              {submittingCreate ? 'Создание...' : 'Сохранить оффер'}
            </button>
          </form>
        ) : null}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Название</th>
                <th>Клиент</th>
                <th>Статус</th>
                <th>Процент</th>
                <th>Баланс</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7">Загрузка офферов...</td>
                </tr>
              ) : sortedOffers.length === 0 ? (
                <tr>
                  <td colSpan="7">Офферов пока нет</td>
                </tr>
              ) : (
                sortedOffers.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.name}</td>
                  <td>{item.client_id}</td>
                  <td>{item.status || 'edit'}</td>
                  <td>{item.parameters ?? 0}</td>
                  <td>{Number(item.balance || 0).toFixed(2)}</td>
                  <td className="offer-actions-cell">
                    <button className="offer-action-button" type="button" onClick={() => openEditModal(item)}>
                      Изменить
                    </button>
                    <button className="offer-action-button danger" type="button" onClick={() => handleDelete(item.id)}>
                      Удалить
                    </button>
                  </td>
                </tr>
              ))) }
            </tbody>
          </table>
        </div>
      </SectionCard>

      {editModalOpen ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Редактирование оффера">
          <div className="modal-card">
            <div className="modal-header">
              <h3>Редактирование оффера #{editOfferId}</h3>
              <button type="button" className="offer-action-button" onClick={closeEditModal}>
                Закрыть
              </button>
            </div>

            <form className="offer-form" onSubmit={handleEditSubmit}>
              <label>
                ID клиента *
                <input name="client_id" value={editForm.client_id} onChange={handleEditChange} required />
              </label>
              <label>
                Название *
                <input name="name" value={editForm.name} onChange={handleEditChange} required />
              </label>
              <label>
                Статус
                <select name="status" value={editForm.status} onChange={handleEditChange}>
                  <option value="edit">edit</option>
                  <option value="active">active</option>
                  <option value="paused">paused</option>
                  <option value="archived">archived</option>
                </select>
              </label>
              <label>
                Тип CPA
                <input name="typecpa" value={editForm.typecpa} onChange={handleEditChange} />
              </label>
              <label>
                Параметры (integer)
                <input name="parameters" value={editForm.parameters} onChange={handleEditChange} />
              </label>
              <label>
                Баланс (float)
                <input name="balance" value={editForm.balance} onChange={handleEditChange} />
              </label>
              <label>
                Базовый URL
                <input name="baseurl" value={editForm.baseurl} onChange={handleEditChange} />
              </label>
              <label>
                URL
                <input name="url" value={editForm.url} onChange={handleEditChange} />
              </label>
              <label>
                Имя бота
                <input name="botname" value={editForm.botname} onChange={handleEditChange} />
              </label>
              <label>
                Тип оффера
                <input name="typeoffer" value={editForm.typeoffer} onChange={handleEditChange} />
              </label>
              <label>
                Секретный ключ
                <input name="secretkey" value={editForm.secretkey} onChange={handleEditChange} />
              </label>
              <label>
                Баннер (URL)
                <input name="banner" value={editForm.banner} onChange={handleEditChange} />
              </label>
              <label className="offer-form-wide">
                Описание
                <textarea name="description" value={editForm.description} onChange={handleEditChange} rows="3" />
              </label>

              <div className="modal-actions">
                <button type="button" className="offer-action-button" onClick={closeEditModal}>
                  Отмена
                </button>
                <button type="submit" className="accent-button" disabled={submittingEdit}>
                  {submittingEdit ? 'Сохранение...' : 'Сохранить изменения'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProductsPage;